import { BriefcaseIcon, UserCheckIcon } from "@phosphor-icons/react";
import { useAuth, useI18N } from "common/hooks";

import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

import { trackOnboardingSignIn } from "@/analytics/onboardingAnalytics";
import OnboardingChoiceStep from "../components/OnboardingChoiceStep";
import { useOnboardingSurface } from "../state/useOnboardingSurface";
import { ONBOARDING_USER_TYPE_OPTIONS } from "../constants";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import type { OnboardingUserType } from "../types";

const ICONS = {
  individual: UserCheckIcon,
  business: BriefcaseIcon,
} as const;

/**
 * Step 1 of 4 — "Hi there! 👋 Let's get started" (`15984:127545`).
 *
 * The branch point, as on native: answering "Business use" swaps the rest of
 * the flow for the claim path ("Let’s find your business") — @see
 * `sequenceForAnswers`.
 *
 * On the docked sheet, a signed-out visitor also gets "Already have an account?
 * Sign in" under Continue — the frame's annotation is "Only on the first page".
 * The full modal never shows it: it only opens after a sign-in. Signing in from
 * here carries the flow on as that modal, from the same step.
 */
const UserTypeStep = () => {
  const i18n = useI18N();
  const { answers, updateAnswers } = useOnboardingFlow();
  const { presentation } = useOnboardingSurface();
  const { isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();

  const signInRow =
    presentation === "docked" && !isLoggedIn ? (
      <p className="flex items-center justify-center gap-2 py-3">
        <span className="body-base-medium text-fg-neutral-strong">
          {i18n.get("alreadyHaveAnAccount")}
        </span>
        <button
          type="button"
          onClick={() => {
            trackOnboardingSignIn();
            showModal(AuthModalScreen.EnterEmail);
          }}
          className="focus-blue label-m-medium min-h-6 rounded-lg text-fg-brand-subtle underline"
        >
          {i18n.get("signIn")}
        </button>
      </p>
    ) : null;

  return (
    <OnboardingChoiceStep<OnboardingUserType>
      groupName="onboarding-user-type"
      title={i18n.get("onboardingUserTypeTitle")}
      subtitle={i18n.get("onboardingUserTypeSubtitle")}
      options={ONBOARDING_USER_TYPE_OPTIONS}
      icons={ICONS}
      value={answers.userType}
      onSelect={(userType) => updateAnswers({ userType })}
      footerExtra={signInRow}
    />
  );
};

export default UserTypeStep;
